import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { updateMe, changePassword } from "../services/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Gem, Cake, PartyPopper, MapPin, Shield, Lock } from "lucide-react";
import { toast } from "sonner";

const fmt = (iso) => new Date(iso).toLocaleDateString(undefined, { dateStyle: "long" });

export default function Profile() {
  const { user, refresh } = useAuth();
  const [firstName, setFirstName] = useState(user.firstName);
  const [lastName, setLastName] = useState(user.lastName);

  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [pwErr, setPwErr] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  const setPwField = (k, v) => setPw((p) => ({ ...p, [k]: v }));

  const save = async () => {
    try {
      await updateMe(firstName, lastName);
      await refresh();
      toast.success("Profile updated.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update profile.");
    }
  };

  const savePassword = async () => {
    setPwErr("");
    if (!pw.current) { setPwErr("Enter your current password."); return; }
    if (pw.next.length < 6) { setPwErr("New password must be at least 6 characters."); return; }
    if (pw.next !== pw.confirm) { setPwErr("New passwords do not match."); return; }
    setPwBusy(true);
    try {
      await changePassword(pw.current, pw.next);
      setPw({ current: "", next: "", confirm: "" });
      toast.success("Password changed successfully.");
    } catch (e) {
      setPwErr(e?.response?.data?.detail || "Could not change password.");
    } finally {
      setPwBusy(false);
    }
  };

  const ro = "rounded-xl bg-vault-panel2/60 border border-white/5 px-3 py-2.5 text-sm text-vault-cream";

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-vault-panel/50">Account</div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-vault-panel mt-1">My Profile</h1>
      </div>

      <div className="rounded-3xl bg-vault-panel p-7 sm:p-8">
        <div className="flex items-center gap-4 mb-7">
          <div className="h-16 w-16 rounded-3xl bg-vault-accent text-vault-panel grid place-items-center font-display font-extrabold text-2xl">{user.firstName[0]}{user.lastName[0]}</div>
          <div>
            <div className="font-display text-2xl font-extrabold text-vault-cream">{user.firstName} {user.lastName}</div>
            <div className="text-sm text-vault-subtle">{user.email}</div>
          </div>
          <div className="ml-auto text-right">
            <div className="text-xs font-bold uppercase tracking-widest text-vault-accent">Gems</div>
            <div className="font-display text-3xl font-extrabold text-vault-cream flex items-center gap-1"><Gem className="h-5 w-5 text-vault-accent" />{user.gemsBalance}</div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-5">
          <div className="space-y-2">
            <Label className="text-vault-subtle">First Name</Label>
            <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} className="bg-vault-panel2 border-white/10 text-vault-cream h-11" />
          </div>
          <div className="space-y-2">
            <Label className="text-vault-subtle">Last Name</Label>
            <Input value={lastName} onChange={(e) => setLastName(e.target.value)} className="bg-vault-panel2 border-white/10 text-vault-cream h-11" />
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-5 mt-5">
          <div><div className="text-xs text-vault-subtle mb-1 flex items-center gap-1"><Shield className="h-3.5 w-3.5" /> Role</div><div className={ro}>{user.role}</div></div>
          <div><div className="text-xs text-vault-subtle mb-1 flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> Primary Location</div><div className={ro}>{user.primaryLocation}</div></div>
          <div><div className="text-xs text-vault-subtle mb-1 flex items-center gap-1"><Cake className="h-3.5 w-3.5" /> Birthdate</div><div className={ro}>{fmt(user.birthdate)}</div></div>
          <div><div className="text-xs text-vault-subtle mb-1 flex items-center gap-1"><PartyPopper className="h-3.5 w-3.5" /> Start Date</div><div className={ro}>{fmt(user.startDate)}</div></div>
        </div>

        <button onClick={save} className="mt-7 rounded-2xl bg-vault-accent text-vault-panel font-bold px-6 py-3 hover:bg-vault-accentdark transition-colors">Save Changes</button>
      </div>

      {/* Change Password */}
      <div className="rounded-3xl bg-vault-panel p-7 sm:p-8">
        <h3 className="font-display text-lg font-bold text-vault-cream mb-1 flex items-center gap-2"><Lock className="h-5 w-5 text-vault-accent" /> Change Password</h3>
        <p className="text-sm text-vault-subtle mb-6">Update your password. You'll keep using your email to sign in.</p>
        <div className="grid sm:grid-cols-3 gap-5">
          <div className="space-y-2">
            <Label className="text-vault-subtle">Current Password</Label>
            <Input type="password" value={pw.current} onChange={(e) => setPwField("current", e.target.value)} className="bg-vault-panel2 border-white/10 text-vault-cream h-11" placeholder="••••••••" />
          </div>
          <div className="space-y-2">
            <Label className="text-vault-subtle">New Password</Label>
            <Input type="password" value={pw.next} onChange={(e) => setPwField("next", e.target.value)} className="bg-vault-panel2 border-white/10 text-vault-cream h-11" placeholder="••••••••" />
          </div>
          <div className="space-y-2">
            <Label className="text-vault-subtle">Confirm New Password</Label>
            <Input type="password" value={pw.confirm} onChange={(e) => setPwField("confirm", e.target.value)} className="bg-vault-panel2 border-white/10 text-vault-cream h-11" placeholder="••••••••" />
          </div>
        </div>
        {pwErr && <div className="text-sm text-red-400 bg-red-950/40 rounded-lg px-3 py-2 mt-4">{pwErr}</div>}
        <button disabled={pwBusy} onClick={savePassword} className="mt-6 rounded-2xl bg-vault-accent text-vault-panel font-bold px-6 py-3 hover:bg-vault-accentdark transition-colors disabled:opacity-60">
          {pwBusy ? "Updating…" : "Update Password"}
        </button>
      </div>
    </div>
  );
}
